import { HisSessionStorageKeys } from '../../core/model/his-session-storage-keys';
import { PersistentValueKey } from '../../core/service/backend/his-persistence.service';
import { deserializeColumnIds, DevTableConfigValues, serializeColumnIds } from './table-model';

// =============================
//             Types
// =============================

/** Represents the keys of the table configuration that can be persisted. */
export type PersistableConfigKey = Exclude<keyof DevTableConfigValues, 'columnsReorderable'>;
/** Represents the values of the table configuration that can be persisted. */
export type PersistableConfigValueMap = Pick<DevTableConfigValues, PersistableConfigKey>;

/** Represents a function that serializes a persistable table configuration value to a string for storage. */
type PersistableValueSerializer<K extends PersistableConfigKey> = ( value: PersistableConfigValueMap[K] ) => string | null;
/** Represents a function that deserializes a string from storage back into a persistable table configuration value. */
type PersistableValueDeserializer<K extends PersistableConfigKey> = ( value: string | null ) => PersistableConfigValueMap[K] | null;

/** Defines the structure of a table aspect, including its key, scope, persistence key, and serialization/deserialization functions. */
export interface TableAspectDefinition<K extends PersistableConfigKey> {
    /** The key of the table configuration aspect. */
    readonly configKey: K;
    /** Indicates whether the aspect is global or specific to a table instance. */
    readonly isGlobal: boolean;
    /** Function to get the persistence key for the aspect based on the table ID. */
    readonly persistenceKey: ( tableId: string ) => PersistentValueKey;
    /** Function to serialize the aspect value for storage. */
    readonly serialize: PersistableValueSerializer<K>;
    /** Function to deserialize the aspect value from storage. */
    readonly deserialize: PersistableValueDeserializer<K>;
}

/** Defines constants used for table configuration, including keys for various aspects of the table's state that can be persisted. */
export enum HisTableConfigConstants {
    /** The global page size default for every table. */
    DEFAULT_TABLE_PAGE_SIZE = 'DefaultTablePageSize',
    /** The page size for a specific table instance. */
    PAGE_SIZE = '.pageSize',
    /** Indicates whether the table filter is shown. */
    SHOW_TABLE_FILTER = '.showTableFilter',
    /** The hidden columns for a specific table instance. */
    HIDDEN_COLS = '.hiddenCols',
    /** The position of the paginator for a specific table instance. */
    PAGINATOR_POSITION = '.paginatorPosition',
    /** The frozen column for a specific table instance. */
    FROZEN_COL = '.frozenCol',
    /** The column order for a specific table instance. */
    COL_ORDER = '.colOrder',
}

// =============================
//            Aspects
// =============================

/** Represents explcitily _no config_ in the DB, to prevent applying frontend defaults. */
const NONE = 'NONE';
/** Represents config keys that are global and not specific to a table instance. */
const GLOBAL_CONFIGS: Set<PersistableConfigKey> = new Set( ['defaultTablePageSize'] );
/** Maps table config keys to their corresponding persistent value key suffixes for user profile and session storage. */
const TABLE_CONFIG_KEY_MAP = {
    frozenCol: { userProfile: HisTableConfigConstants.FROZEN_COL, sessionStorage: HisSessionStorageKeys.CS_SYS_ANONYMOUS_FROZEN_COL },
    hiddenCols: { userProfile: HisTableConfigConstants.HIDDEN_COLS, sessionStorage: HisSessionStorageKeys.CS_SYS_ANONYMOUS_HIDDEN_COLS },
    colOrder: { userProfile: HisTableConfigConstants.COL_ORDER, sessionStorage: HisSessionStorageKeys.CS_SYS_ANONYMOUS_COL_ORDER },
    paginatorPosition: { userProfile: HisTableConfigConstants.PAGINATOR_POSITION, sessionStorage: HisSessionStorageKeys.CS_SYS_ANONYMOUS_PAGINATOR_POSITION },
    showTableFilter: { userProfile: HisTableConfigConstants.SHOW_TABLE_FILTER, sessionStorage: HisSessionStorageKeys.CS_SYS_ANONYMOUS_SHOW_TABLE_FILTER },
    defaultTablePageSize: { userProfile: HisTableConfigConstants.DEFAULT_TABLE_PAGE_SIZE, sessionStorage: HisSessionStorageKeys.CS_SYS_ANONYMOUS_DEFAULT_TABLE_PAGE_SIZE },
    tablePageSize: { userProfile: HisTableConfigConstants.PAGE_SIZE, sessionStorage: HisSessionStorageKeys.CS_SYS_ANONYMOUS_PAGE_SIZE }
} as const satisfies { [key in PersistableConfigKey]: PersistentValueKey };

/** Defines the aspects of the table configuration that can be persisted, including how to serialize and deserialize each aspect for storage. */
export const TABLE_CONFIG_ASPECTS = {
    frozenCol: createAspectDefinition( 'frozenCol' ),
    hiddenCols: createAspectDefinition( 'hiddenCols', serializeArray( true ), deserializeArray( true ) ),
    colOrder: createAspectDefinition( 'colOrder', serializeArray( false ), deserializeArray( false ) ),
    paginatorPosition: createAspectDefinition( 'paginatorPosition' ),
    defaultTablePageSize: createAspectDefinition( 'defaultTablePageSize', keepNull( String ), deserializeNumber ),
    tablePageSize: createAspectDefinition( 'tablePageSize', keepNull( String ), deserializeNumber ),
    showTableFilter: createAspectDefinition( 'showTableFilter', keepNull( v => v ? 'true' : 'false' ), deserializeBoolean )
} as const satisfies { [K in PersistableConfigKey]: TableAspectDefinition<K> };
export const TABLE_CONFIG_KEYS = ['frozenCol', 'hiddenCols', 'colOrder', 'paginatorPosition', 'showTableFilter', 'defaultTablePageSize', 'tablePageSize'] as const satisfies PersistableConfigKey[];

// =============================
//           Functions
// =============================

export function isTableConfigKey( key: string ): key is PersistableConfigKey {
    return TABLE_CONFIG_KEYS.includes( key as PersistableConfigKey );
}

export function isGlobalTableConfigKey( key: string ): key is PersistableConfigKey {
    return GLOBAL_CONFIGS.has( key as PersistableConfigKey );
}

/**
 * Retrieves the definition of a specific table configuration aspect, including its serialization and deserialization methods.
 * @param key The key of the table configuration aspect to retrieve.
 * @returns The definition of the table configuration aspect.
 */
export function tableConfigAspect<K extends PersistableConfigKey>( key: K ): TableAspectDefinition<K> {
    return TABLE_CONFIG_ASPECTS[key] as any;
}

function createAspectDefinition<K extends PersistableConfigKey>( configKey: K, serialize?: PersistableValueSerializer<K>, deserialize?: PersistableValueDeserializer<K> ): TableAspectDefinition<K> {
    return {
        configKey,
        isGlobal: GLOBAL_CONFIGS.has( configKey ),
        persistenceKey: ( tableId: string ) => toPersistenceKey( configKey, tableId, GLOBAL_CONFIGS.has( configKey ) ),
        serialize: serialize ?? keepNull( value => value as string | null ),
        deserialize: deserialize ?? ( value => value as PersistableConfigValueMap[K] | null ),
    };
}

function keepNull<T>( serialize: ( value: T ) => string | null ): ( value: T | null ) => string | null {
    return function( value: T | null ): string | null {
        return value == null ? null : serialize( value );
    };
}

function serializeArray( useNoneForEmpty: boolean ) {
    return function( value: string[] ): string | null {
        if ( !value || value.length === 0 ) {
            return useNoneForEmpty ? NONE : null;
        }
        return serializeColumnIds( value );
    };
}

function deserializeArray( treatNoneAsEmpty: boolean ) {
    return function( value: string | null ): string[] | null {
        if ( value === null ) {
            return null;
        }
        if ( treatNoneAsEmpty && value === NONE ) {
            return [];
        }
        return deserializeColumnIds( value );
    };
}

function deserializeNumber( value: string | null ): number | null {
    if ( value === null ) {
        return null;
    }
    const parsed = Number( value );
    return isFinite( parsed ) ? parsed : null;
}

function deserializeBoolean( value: string | null ): boolean | null {
    if ( value === null ) {
        return null;
    }
    return value === 'true' ? true : value === 'false' ? false : null;
}

function toPersistenceKey( configKey: PersistableConfigKey, tableId: string, isGlobal: boolean ): PersistentValueKey {
    const { userProfile, sessionStorage } = TABLE_CONFIG_KEY_MAP[configKey];
    return {
        sessionStorage: buildScopedStorageKey( tableId, sessionStorage, isGlobal ),
        userProfile: buildScopedStorageKey( tableId, userProfile, isGlobal )
    };
}

function buildScopedStorageKey( tableId: string, key: string, isGlobal: boolean ): string {
    return isGlobal ? key : `${tableId}${key}`;
}